import type { AdminFile, FileMimeType, FileVisibility } from '@mds/shared/types/file';

import * as files from './files.api';
import { makePreview } from './preview';
import { isConvertible, toWebp } from './transcode';
import { makeVariants } from './variants';

/**
 * The browser side of the presigned upload.
 *
 * Three steps, and the middle one deliberately does not go through the shared
 * API client: that raw PUT goes to the storage provider, not to MDS, and the
 * signature covers the exact header set the API handed back. Sending cookies or
 * an extra header there fails the signature check, so it uses XMLHttpRequest
 * with `withCredentials` off — which also gives the progress events.
 */

/** What the API accepts. SVG is excluded on purpose: it can carry active content. */
export const ACCEPTED_TYPES: FileMimeType[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

/**
 * Ceiling on what may be *chosen*, not on what is uploaded. Images are
 * re-encoded to WebP before anything leaves the browser, so a 20 MB photo is
 * fine; this only stops a file so large that decoding it would exhaust memory.
 */
const MAX_SOURCE_IMAGE_BYTES = 100 * 1024 * 1024;

/** The server's own limit for this type, so the browser can fail fast. */
export const limitFor = (mimeType: string) =>
  mimeType === 'application/pdf' ? MAX_DOCUMENT_BYTES : MAX_IMAGE_BYTES;

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

/**
 * Rejects a file the API would refuse, without spending a round trip on it.
 *
 * Images are checked against the much larger source ceiling rather than the
 * API's 8 MB limit: conversion runs first, and it is the converted size that
 * has to fit.
 */
export function checkFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type as FileMimeType)) {
    return `${file.type || 'That file type'} is not accepted. Use JPEG, PNG, WebP or PDF.`;
  }
  const limit = isConvertible(file.type) ? MAX_SOURCE_IMAGE_BYTES : limitFor(file.type);
  if (file.size > limit) {
    return `${formatBytes(file.size)} is over the ${formatBytes(limit)} limit for this type.`;
  }
  return null;
}

/** Lowercase hex SHA-256 of the whole body, which is what the API compares against. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** The raw PUT, reported as 0–1. Rejects on any non-2xx or a network failure. */
function putToStorage(
  url: string,
  method: string,
  headers: Record<string, string>,
  body: ArrayBuffer,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    // Never send cookies to the storage provider: they are not part of the
    // signature and some providers reject the request outright.
    xhr.withCredentials = false;
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`The storage provider rejected the upload (${xhr.status}).`));
    xhr.onerror = () => reject(new Error('The upload could not reach the storage provider.'));
    xhr.onabort = () => reject(new Error('The upload was cancelled.'));
    xhr.send(body);
  });
}

/**
 * Draws a preview of `source` and stores it as the file's preview, by the same
 * three steps as the file itself. Resolves with the file, now carrying its
 * preview URL.
 */
export async function uploadPreview(
  fileId: string,
  source: Blob,
  mimeType: string,
): Promise<AdminFile> {
  const preview = await makePreview(source, mimeType);
  const bytes = await preview.arrayBuffer();
  const upload = await files.initiateThumbnail(fileId, {
    sizeBytes: bytes.byteLength,
    checksumSha256: await sha256Hex(bytes),
  });
  await putToStorage(upload.url, upload.method, upload.headers, bytes, () => undefined);
  return files.completeThumbnail(fileId);
}

/**
 * Makes the display sizes of an image and stores each one by the same three
 * steps as the file. Resolves with the file as the API last returned it.
 */
export async function uploadVariants(fileId: string, source: Blob): Promise<AdminFile | null> {
  let latest: AdminFile | null = null;
  for (const { width, blob } of await makeVariants(source)) {
    const bytes = await blob.arrayBuffer();
    const upload = await files.initiateVariant(fileId, {
      width,
      sizeBytes: bytes.byteLength,
      checksumSha256: await sha256Hex(bytes),
    });
    await putToStorage(upload.url, upload.method, upload.headers, bytes, () => undefined);
    latest = await files.completeVariant(fileId, width);
  }
  return latest;
}

export type UploadPhase =
  'converting' | 'hashing' | 'uploading' | 'verifying' | 'previewing' | 'sizing';

/**
 * Runs the whole flow for one file, reporting the phase and its progress.
 * `folderId` null puts it at the root of the library.
 */
export async function uploadFile(
  file: File,
  visibility: FileVisibility,
  onProgress: (phase: UploadPhase, fraction: number) => void,
  folderId: string | null = null,
): Promise<AdminFile> {
  // Convert before hashing: the checksum and byte length the API authorizes
  // must describe the bytes actually sent, which are the converted ones.
  onProgress('converting', 0);
  const prepared = await toWebp(file, MAX_IMAGE_BYTES);

  onProgress('hashing', 0);
  const bytes = await prepared.arrayBuffer();
  const checksumSha256 = await sha256Hex(bytes);

  const initiated = await files.initiateUpload({
    originalFilename: prepared.name,
    mimeType: prepared.type,
    sizeBytes: prepared.size,
    checksumSha256,
    visibility,
    folderId,
  });

  onProgress('uploading', 0);
  await putToStorage(
    initiated.upload.url,
    initiated.upload.method,
    initiated.upload.headers,
    bytes,
    (fraction) => onProgress('uploading', fraction),
  );

  // Completion is idempotent, so a retry here cannot double-count.
  onProgress('verifying', 1);
  const ready = await files.completeUpload(initiated.file.id);

  // The file is stored and usable by now, so a preview or display size that
  // cannot be made leaves it without one rather than failing the upload. The
  // Files page tries again the next time it shows the file.
  onProgress('previewing', 1);
  let result = ready;
  try {
    result = await uploadPreview(ready.id, prepared, prepared.type);
  } catch {
    // Kept without a preview.
  }

  if (isConvertible(prepared.type)) {
    onProgress('sizing', 1);
    try {
      result = (await uploadVariants(result.id, prepared)) ?? result;
    } catch {
      // Kept without display sizes; public pages get the original meanwhile.
    }
  }
  return result;
}
